import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

export type StatTone = 'neutral' | 'critical' | 'warning' | 'good';

const TONE_DOT: Record<StatTone, string> = {
  neutral: 'bg-fg-subtle',
  critical: 'bg-sev-1',
  warning: 'bg-sev-3',
  good: 'bg-sev-4',
};

interface StatTileProps {
  label: string;
  value: ReactNode;
  /** Secondary line under the value — a ratio, a breakdown, a qualifier. */
  detail?: ReactNode;
  /**
   * Status of the thing being counted. Renders a coloured dot beside the label;
   * the value itself stays in normal ink deliberately, so the tile is readable
   * without colour vision and a screenful of tiles doesn't turn into a traffic
   * light. `toneLabel` is what makes the dot mean something.
   */
  tone?: StatTone;
  /** Accessible text for the tone dot, e.g. "critical". Required with `tone`. */
  toneLabel?: string;
  /**
   * Figure style for the headline value. Tabular figures line up in a column,
   * which is what a table wants and what a single headline number does not —
   * the fixed advance width leaves gaps around a `1` and makes one number read
   * as a fragment of a table that is not there. Defaults to `tabular` because
   * that is what the three pages already using this tile ship today; new
   * surfaces should pass `proportional`.
   */
  figures?: 'tabular' | 'proportional';
  className?: string;
}

/**
 * One headline number.
 *
 * A handful of these is a KPI row — the right form for "a few numbers that lead
 * a dashboard", where a chart would add ink without adding meaning.
 *
 * Extracted because this markup had been copy-pasted into three pages before
 * anything needed a fourth.
 */
export function StatTile({ label, value, detail, tone = 'neutral', toneLabel, figures = 'tabular', className }: StatTileProps) {
  return (
    <div className={cn('rounded-lg border border-border bg-card p-4', className)}>
      <div className="flex items-center gap-1.5">
        {tone !== 'neutral' ? (
          <span className={cn('inline-block w-1.5 h-1.5 rounded-full shrink-0', TONE_DOT[tone])} aria-hidden="true" />
        ) : null}
        <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
          {label}
          {tone !== 'neutral' && toneLabel ? <span className="sr-only"> — {toneLabel}</span> : null}
        </p>
      </div>
      <p className={cn('text-2xl font-semibold mt-1', figures === 'tabular' && 'tabular-nums')}>{value}</p>
      {detail ? <p className="text-xs text-muted-foreground mt-0.5">{detail}</p> : null}
    </div>
  );
}
